import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import TourCard from '../tour/components/card';
import TrafficCard from '../traffic/components/card';
import EventCard from '../event/components/card';
import SearchIcon from '../../assets/search-icon';
import LabelSocialMediaIcon from '../../components/label-social-media-icon';
import Slider from '../../components/slider';
import useDetectIsMobile from '../../hook/use-detect-is-mobile';
import { TransportServices } from '../trans-schedule/search-transport';
import Title from './components/title';
import { API, ROUTES } from '../../helpers/constants';
import Dropdown from '../../components/dropdown';
import { redirect } from '../../helpers/link';
import LabelAmount from '../../components/label-amount-icon';
import { convertToArray } from '../../helpers/array';
import useGet from '../../hook/use-get';
import EventLoadingItem from '../event/components/loading-list-item';
import TourLoadingItem from '../tour/components/loading-list-item';
import TrafficLoadingItem from '../traffic/components/loading-list-item';
import { SectionError } from '../../components/error';
import InstagramIcon from '../../assets/instagram-icon';
import YoutubeIcon from '../../assets/youtube-icon';
import TwitterIcon from '../../assets/twitter-icon';

const Event = ({ navigate }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'eventHome',
    api: API.events + '?group=1'
  });

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="grid grid-cols-1 w-full mx-auto md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array(4)
            .fill(0)
            .map(() => (
              <EventLoadingItem />
            ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="h-80 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetch} />
        </div>
      );
    }

    return (
      <div className="w-full mx-auto gap-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {data?.map((event) => (
          <EventCard
            title={event?.name}
            subtitle={event?.description}
            sourceImage={event?.['file_url']}
            onClick={() => navigate(`${ROUTES.event}/${event?.id}`)}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="event" className="pt-8 pb-14 w-[80%] m-auto">
      <Title title="Update Terbaru" isPrimary onClick={() => navigate(ROUTES.event)} />
      {renderContent()}
    </section>
  );
};

const Tour = ({ navigate }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'tourHome',
    api: API.tours + '?group=1'
  });

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="grid grid-cols-1 w-full mx-auto md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array(3)
            .fill(0)
            .map(() => (
              <TourLoadingItem />
            ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="h-80 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetch} />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 w-full mx-auto md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data?.map((item) => (
          <TourCard
            key={item?.['city_id']}
            name={item?.name}
            sourceImage={item?.['file_url']}
            onClick={() => navigate(`${ROUTES.tour}/${item?.id}`)}
            location={item?.['city_name']}
            capacity={item?.kapasitas}
            facilities={item?.facility}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="tourism-priority" className="pt-8 pb-14 w-[80%] m-auto">
      <Title
        title="Kawasan Wisata Prioritas"
        subtitle="Temukan wisata menarik di kota/kabupaten tujuan perjalanan anda"
        onClick={() => navigate(ROUTES.tour)}
      />
      {renderContent()}
    </section>
  );
};

const SocialMedia = ({ isTablet, isMobile }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'socMedHome',
    api: API.socMeds + '?id=1'
  });

  const socMed = data?.map((item) => {
    if (item?.name === 'youtube') return item;
  });

  const socMedAccount = [
    { label: 'Instagram', icon: <InstagramIcon />, link: socMed?.[0]?.['instagram_url'] },
    { label: 'Youtube', icon: <YoutubeIcon />, link: socMed?.[0]?.['youtube_url'] },
    { label: 'Twitter', icon: <TwitterIcon />, link: socMed?.[0]?.['twitter_url'] }
  ];

  const getIframeHeight = () => {
    if (isTablet) return 380;
    if (isMobile) return 320;

    return undefined;
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <>
          <div className="grid grid-cols-2 gap-6">
            <div className="w-full lg:h-[380px] rounded-lg bg-gray-300 animate-pulse" />
            <div className="w-full lg:h-[380px] rounded-lg bg-gray-300 animate-pulse" />
          </div>
          <div className="w-full h-12 bg-gray-300 animate-pulse" />
        </>
      );
    }

    if (isError) {
      return (
        <div className="h-80 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetch} />
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col md:flex-row gap-6 lg:h-[520px] w-full">
          <iframe
            className="w-full rounded-lg"
            height={getIframeHeight()}
            src={socMed?.[0]?.['link_url']}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
          <iframe
            height={getIframeHeight()}
            allowTransparency="true"
            allowFullScreen={true}
            className="w-full"
            title="Twitter Timeline"
            src="https://syndication.twitter.com/srv/timeline-profile/screen-name/dishub_jabar?dnt=true&amp;embedId=twitter-widget-0&amp;features=eyJ0ZndfdGltZWxpbmVfbGlzdCI6eyJidWNrZXQiOltdLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X2ZvbGxvd2VyX2NvdW50X3N1bnNldCI6eyJidWNrZXQiOnRydWUsInZlcnNpb24iOm51bGx9LCJ0ZndfdHdlZXRfZWRpdF9iYWNrZW5kIjp7ImJ1Y2tldCI6Im9uIiwidmVyc2lvbiI6bnVsbH0sInRmd19yZWZzcmNfc2Vzc2lvbiI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfZm9zbnJfc29mdF9pbnRlcnZlbnRpb25zX2VuYWJsZWQiOnsiYnVja2V0Ijoib24iLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X21peGVkX21lZGlhXzE1ODk3Ijp7ImJ1Y2tldCI6InRyZWF0bWVudCIsInZlcnNpb24iOm51bGx9LCJ0ZndfZXhwZXJpbWVudHNfY29va2llX2V4cGlyYXRpb24iOnsiYnVja2V0IjoxMjA5NjAwLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X3Nob3dfYmlyZHdhdGNoX3Bpdm90c19lbmFibGVkIjp7ImJ1Y2tldCI6Im9uIiwidmVyc2lvbiI6bnVsbH0sInRmd19kdXBsaWNhdGVfc2NyaWJlc190b19zZXR0aW5ncyI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfdXNlX3Byb2ZpbGVfaW1hZ2Vfc2hhcGVfZW5hYmxlZCI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9LCJ0ZndfdmlkZW9faGxzX2R5bmFtaWNfbWFuaWZlc3RzXzE1MDgyIjp7ImJ1Y2tldCI6InRydWVfYml0cmF0ZSIsInZlcnNpb24iOm51bGx9LCJ0ZndfbGVnYWN5X3RpbWVsaW5lX3N1bnNldCI6eyJidWNrZXQiOnRydWUsInZlcnNpb24iOm51bGx9LCJ0ZndfdHdlZXRfZWRpdF9mcm9udGVuZCI6eyJidWNrZXQiOiJvbiIsInZlcnNpb24iOm51bGx9fQ%3D%3D&amp;frame=false&amp;hideBorder=false&amp;hideFooter=false&amp;hideHeader=false&amp;hideScrollBar=false&amp;lang=en&amp;maxHeight=520px&amp;origin=https%3A%2F%2Fsip-dishub.jabarprov.go.id%2F%23%2F&amp;sessionId=3885abdaa6cf160e63164fb87b240017924e8090&amp;showHeader=true&amp;showReplies=false&amp;theme=light&amp;transparent=false&amp;widgetsVersion=01917f4d1d4cb%3A1696883169554"
          ></iframe>
        </div>
        <div className="space-y-4 md:space-y-0 md:flex md:justify-center md:items-center md:gap-x-14">
          <h3 className="text-base mx-2 font-semibold">
            Ikuti berita terbaru lewat sosial media Dishub Jabar
          </h3>
          <div className="flex flex-wrap justify-center gap-2">
            {socMedAccount?.map((item) => (
              <LabelSocialMediaIcon
                key={item?.label}
                label={item?.label}
                icon={item?.icon}
                link={item?.link}
              />
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <section id="social-media" className="pt-3 md:pt-0 pb-14">
      <Title title="Social Media Update" />
      <div className="w-[90%] m-auto flex flex-wrap md:mt-10 md:flex-col space-y-6 justify-center">
        {renderContent()}
      </div>
    </section>
  );
};

const SearchTransportation = ({ navigate }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'destination',
    api: API.destinationOptions
  });

  const options = data?.map((item) => ({
    label: item?.['destination_name'],
    value: item?.['destination_name']
  }));

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm();

  const onSubmit = (value) => {
    navigate(`${ROUTES.search_schedule}?asal=${value?.origin}&tujuan=${value?.destination}`);
  };

  if (isFetching) {
    return <div className="w-full h-96 bg-gray-300 animate-pulse" />;
  }

  if (isError) {
    return (
      <div className="h-80 items-center justify-center flex border border-b rounded-lg">
        <SectionError refetch={refetch} />
      </div>
    );
  }

  return (
    <section
      id="trans-info"
      className="w-full bg-cover"
      style={{ backgroundImage: 'url(/bus-angkutan.jpg)' }}
    >
      <div className="py-16 px-10 lg:py-20 md:px-16 lg:px-28 flex flex-col md:flex-row justify-center md:justify-between items-center h-full w-full">
        <div className="w-full md:w-[50%] my-5">
          <h2 className={`text-2xl md:text-4xl font-bold text-white md:text-left`}>
            Mau Pergi Kemana?
          </h2>
          <p className="text-sm mb-3 mt-5 text-white md:text-left">
            Cari jadwal angkutan dengan jadwal perjalanan kamu.
            Nikmati kemudahan untuk memilih layanan angkutan umum apapun sesuai dengan kebutuhanmu.
          </p>
        </div>
        <div className="w-full md:w-[50%] md:flex md:justify-end">
          <form
            className="flex flex-col bg-white justify-between rounded-lg p-5 md:w-[80%]"
            onSubmit={handleSubmit(onSubmit)}
          >
            <h3 className="text-2xl md:text-3xl mb-8 text-left">Yuk! Naik Angkutan Umum Aja</h3>
            <Dropdown
              name="origin"
              label="Asal Kota/ Kabupaten"
              placeholder="Masukan asal keberangkatanmu"
              values={options}
              rules={{ required: true }}
              control={control}
              error={errors?.origin}
              isFindTransport
            />
            <div className="my-5">
              <Dropdown
                name="destination"
                label="Tujuan Kota/ Kabupaten"
                placeholder="Masukkan tempat tujuanmu"
                values={options}
                rules={{ required: true }}
                control={control}
                error={errors?.destination}
                isFindTransport
              />
            </div>
            <button className="flex bg-secondary-500 rounded-lg p-3 justify-center items-center space-x-2">
              <SearchIcon />
              <span>Cari</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Traffic = ({ navigate }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'trafficHome',
    api: API.traffics + '?group=1'
  });

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="w-[80%] m-auto grid grid-cols-1 lg:grid-cols-2 md:px-28 justify-center gap-6">
          {Array(4)
            .fill(0)
            .map(() => (
              <TrafficLoadingItem />
            ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="h-80 w-[80%] m-auto items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetch} classNames="text-white" />
        </div>
      );
    }

    return (
      <div className="w-[80%] m-auto grid grid-cols-1 lg:grid-cols-2 md:px-28 justify-center gap-6">
        {data?.map((item) => (
          <TrafficCard
            title={item?.name}
            sourceImage={item?.['file_url']}
            onClick={() => redirect(item?.['link_url'], '_blank')}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="traffic" className="bg-primary-500 pt-8 pb-14">
      <Title
        title="Lalu Lintas"
        textColor="white"
        subtitle="Dapatkan informasi lalu lintas terbaru dan pembaruan kondisi jalan."
        onClick={() => navigate(ROUTES.traffic)}
      />
      {renderContent()}
    </section>
  );
};

const Banner = ({ isMobile }) => {
  const [banners, setBanners] = useState([]);
  const {
    isFetching: isFetching,
    isError,
    refetch
  } = useGet({
    queryKey: 'bannerHome',
    api: API.banners,
    options: {
      onSuccess: (res) => {
        const result = [];
        res?.data?.forEach((item) => {
          result.push(item?.[isMobile ? 'file_url_potrait' : 'file_url']);
        });
        setBanners(result);
      }
    }
  });

  if (isFetching) {
    return <div className="w-full h-96 bg-gray-300 animate-pulse" />;
  }

  if (isError) {
    return (
      <div className="h-80 items-center justify-center flex border border-b rounded-lg">
        <SectionError refetch={refetch} />
      </div>
    );
  }

  return <Slider contents={convertToArray(banners)} isArrowRounded isShowIndicator />;
};

const Home = () => {
  const navigate = useNavigate();
  const { isTablet, isMobile } = useDetectIsMobile();

  return (
    <div className="flex-col justify-center items-center">
      <Banner isMobile={isMobile} />
      <section className="pt-24 pb-12 w-[90%] md:w-[80%] m-auto">
        <TransportServices />
        <hr className="my-10" />
        <div className="w-[80%] m-auto">
          <LabelAmount />
        </div>
      </section>
      <SearchTransportation navigate={navigate} />
      <Event navigate={navigate} />
      <SocialMedia isTablet={isTablet} isMobile={isMobile} />
      <Traffic navigate={navigate} />
      <Tour navigate={navigate} />
    </div>
  );
};

export default Home;
